import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  MARK_ARC_STROKE_D,
  MARK_ARC_STROKE_WIDTH,
  MARK_DOT,
  MARK_VIEWBOX,
  WORDMARK_D,
  WORDMARK_VIEWBOX,
  BRAND,
} from '@/lib/brand-assets';

interface WelcomeScreenProps {
  onComplete: () => void;
}

// ─── Logo animation, in seconds ───────────────────────────────
// The arc is the last thing to finish, so the logo is "complete" when the arc
// lands: ARC_DELAY + ARC_DUR.
const DOT_DUR   = 0.45;
const ARC_DELAY = 0.35;
const ARC_DUR   = 1.2;
/** When the mark is fully formed — 1.55s. */
const LOGO_COMPLETE_S = ARC_DELAY + ARC_DUR;

// The wordmark slide takes exactly as long as the logo took to form, and starts
// the instant the logo lands. Both are derived from LOGO_COMPLETE_S rather than
// hardcoded, so the two halves can never drift apart again.
//
// Previously the slide was a fixed 0.55s fired at a hardcoded 2.8s — 1.25s
// AFTER the arc had already finished at 1.55s. That gap was the pause: the mark
// sat there completed, doing nothing, before the name appeared.
const SLIDE_DUR = LOGO_COMPLETE_S;

// ─── Timing (ms) ──────────────────────────────────────────────
// 0s       dot fades in, arc draws (one unbroken stroke)
// 1.55s    arc lands AND the wordmark starts sliding out — no gap
// 3.10s    wordmark lands
// 6.2s     tagline
// 8.4s     hand off
const TIMING = {
  expand:   LOGO_COMPLETE_S * 1000,
  tagline:  6200,
  complete: 8400,
};

// Decisive, mechanical ease — fast commit, clean settle. Right for the arc,
// which draws at a steady visual rate.
const REVEAL_EASE = [0.4, 0, 0.2, 1] as const;

// The slide gets a gentler, more symmetric curve. REVEAL_EASE front-loads the
// travel: with it, the wordmark covered its whole distance in roughly the first
// third and READ as a ~550ms move no matter what duration was configured —
// which defeats the point of matching the logo's duration. This spreads the
// movement across the full SLIDE_DUR so the slide genuinely takes as long as
// the logo took to form.
const SLIDE_EASE = [0.45, 0, 0.55, 1] as const;

export function WelcomeScreen({ onComplete }: WelcomeScreenProps) {
  const [expand, setExpand] = useState(false);
  const [phase, setPhase]   = useState(0);
  const reduce = useReducedMotion();

  // Animate maxWidth to the wordmark's REAL width, not a fixed 400px.
  // The artwork renders ~240px on a phone, so animating to 400 meant the reveal
  // visually finished at ~60% of the value and the remaining 40% of the
  // animation played out invisibly — the slide looked like 530ms however long
  // the transition actually was. Measuring makes the visible slide and the
  // configured duration the same thing.
  const wordmarkRef = useRef<SVGSVGElement>(null);
  const [wordmarkW, setWordmarkW] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const el = wordmarkRef.current;
      if (el) setWordmarkW(el.getBoundingClientRect().width);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    const expandAt = TIMING.expand;
    const t = [
      setTimeout(() => setExpand(true), expandAt),
      setTimeout(() => setPhase(1), TIMING.tagline),
      setTimeout(onComplete,        TIMING.complete),
    ];
    return () => t.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ background: BRAND.navy900 }}
    >
      {/* Notebook dot-grid — the site's hero texture, on the dark surface */}
      <div className="absolute inset-0 paper-dots-dark pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <h1 className="sr-only">Cover My Class</h1>

        {/* ── Reversed lock-up: mark draws, wordmark reveals ── */}
        <div
          className="flex items-center justify-center"
          role="img"
          aria-label="Cover My Class"
          style={{ gap: 'clamp(14px, 2.5vw, 22px)' }}
        >
          <svg
            viewBox={MARK_VIEWBOX}
            aria-hidden="true"
            style={{ width: 'clamp(64px, 13vw, 104px)', height: 'auto', display: 'block', flexShrink: 0 }}
          >
            {/* Arc reversed: cream stroke on navy */}
            <motion.path
              d={MARK_ARC_STROKE_D}
              fill="none"
              stroke={BRAND.cream}
              strokeWidth={MARK_ARC_STROKE_WIDTH}
              strokeLinecap="round"
              initial={reduce ? { pathLength: 1 } : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={reduce ? { duration: 0 } : { duration: ARC_DUR, delay: ARC_DELAY, ease: REVEAL_EASE }}
            />
            {/* Dot stays Sky 500 — the class, sheltered */}
            <motion.circle
              cx={MARK_DOT.cx}
              cy={MARK_DOT.cy}
              r={MARK_DOT.r}
              fill={BRAND.navy500}
              initial={reduce ? { opacity: 1 } : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={reduce ? { duration: 0 } : { duration: DOT_DUR, ease: 'easeOut' }}
            />
          </svg>

          {/* Wordmark reveals left → right; flex recentres as it grows */}
          <motion.span
            style={{ overflow: 'hidden', display: 'inline-block', whiteSpace: 'nowrap' }}
            initial={{ maxWidth: reduce ? wordmarkW : 0 }}
            animate={{ maxWidth: expand || reduce ? wordmarkW : 0 }}
            transition={{ duration: SLIDE_DUR, ease: SLIDE_EASE }}
          >
            <svg
              ref={wordmarkRef}
              viewBox={WORDMARK_VIEWBOX}
              aria-hidden="true"
              style={{ width: 'clamp(240px, 46vw, 380px)', height: 'auto', display: 'block' }}
            >
              <path d={WORDMARK_D} fill={BRAND.cream} />
            </svg>
          </motion.span>
        </div>

        {/* Tagline — the brand line, one gilded detail */}
        {phase >= 1 && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="mt-8"
          >
            <div
              className="mx-auto mb-5"
              style={{ width: 44, height: 2, background: BRAND.gold, borderRadius: 1 }}
              aria-hidden="true"
            />
            {/* X2: reverted. The film's end card line ran to two lines on a
                phone and read long here; the short strapline lands better as a
                closing beat. */}
            <p
              className="font-display text-xl md:text-2xl"
              style={{ color: 'rgba(247, 241, 229, 0.92)' }}
            >
              Calm cover. Every time.
            </p>
          </motion.div>
        )}

        {/* Loading dots */}
        {expand && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="mt-12 flex justify-center gap-2"
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ background: 'rgba(247, 241, 229, 0.6)' }}
                animate={reduce ? {} : { opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
